import { Component, computed, effect, inject, input, output, signal, Signal, WritableSignal } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanGasWarningCenterDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-gas-warning-center-detail-form/floorplan-gas-warning-center-detail-form.component";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { ProductItem } from "@project/product-item";
import { ProjectStateService } from "@project/project-state.service";
import { take } from "rxjs/operators";

@Component({
  selector: " app-gas-warning-center-notes",
  templateUrl: "./gas-warning-center-notes.component.html",
  imports: [
    UiKitModule,
    FloorplanItemDetailsComponent,
    FloorplanGasWarningCenterDetailFormComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
  ],
  standalone: true,
})
export class GasWarningCenterNotesComponent {
  private readonly projectStateService = inject(ProjectStateService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();

  protected readonly gasWarningCenter = computed(() => this.floorplanItem() as FloorplanGasWarningCenter);
  protected readonly isDiscontinued: WritableSignal<boolean> = signal(false);
  protected readonly productItems = computed(() => this.getProductItems(this.gasWarningCenter()));
  protected readonly attachments: Signal<ProductItem[]> = computed(() => this.gasWarningCenter().config.attachments);

  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  constructor() {
    effect(() => {
      const gasWarningCenter = this.gasWarningCenter();
      this.projectStateService
        .isConfigDiscontinued(gasWarningCenter.config.id)
        .pipe(take(1))
        .subscribe((isDiscontinued) => {
          this.isDiscontinued.set(isDiscontinued);
        });
    });
  }

  protected getProductItems(floorplanGasWarningCenter: FloorplanGasWarningCenter): ProductItem[] {
    if (floorplanGasWarningCenter.config.regard3000Configuration) {
      const data = [
        ...floorplanGasWarningCenter.config.regard3000Configuration.baseUnits,
        ...(floorplanGasWarningCenter.config.regard3000Configuration.inputModules ?? []),
        ...(floorplanGasWarningCenter.config.regard3000Configuration.relayModules ?? []),
        ...(floorplanGasWarningCenter.config.regard3000Configuration.gatewayModules ?? []),
        ...(floorplanGasWarningCenter.config.regard3000Configuration.lidUnits ?? []),
      ];
      const slotCovers = floorplanGasWarningCenter.config.regard3000Configuration.slotCovers;
      if (slotCovers) {
        data.push(slotCovers);
      }
      return data;
    }
    if (floorplanGasWarningCenter.config.regard7000PlusConfiguration) {
      return [
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.dockingStations,
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.inputModules,
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.relayModules,
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.gatewayModules,
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.terminalBlocks,
        ...floorplanGasWarningCenter.config.regard7000PlusConfiguration.dashboards,
      ];
    }
    return [ProductItem.create(floorplanGasWarningCenter.config.productId)];
  }
}
